import React, { useState, useEffect } from 'react';
import { Form, Button, Card, Row, Col, Spinner, Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { BsInfoCircle } from 'react-icons/bs';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, faPlus, faMinus, faTrash, faSave, 
  faTimes, faLayerGroup, faCubes, faTag, faWonSign 
} from '@fortawesome/free-solid-svg-icons';
import { AiFillCloseCircle } from 'react-icons/ai';
import CheckOutModal from 'components/modal/CheckOutModal';
import { handleValueDelete } from 'components/common/SunnyUtils';
import styled from 'styled-components';
import { fetchServiceItems, createPricingPolicy, fetchPricingPolicy } from 'hooks/CallApi';

// --- Styled Components ---
const PageContainer = styled.div`
  padding: 2rem 1rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const BackButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #64748b;
  font-weight: 600;
  padding: 0;
  background: transparent;
  border: none;
  
  &:hover, &:focus, &:active {
    color: #3b82f6;
    background: transparent !important;
    box-shadow: none !important;
    transform: translateX(-4px);
  }
  transition: all 0.2s ease;
`;

const StyledCard = styled(Card)`
  border: none;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  margin-bottom: 2rem;
`;

const CardHeader = styled.div`
  padding: 1.5rem 2rem;
  background: linear-gradient(to right, #f8fafc, #ffffff);
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PolicyTitle = styled.h3`
  margin: 0;
  font-weight: 700;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ActionButton = styled(Button)`
  border-radius: 8px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0.6rem 1.2rem;
`;

const FormSection = styled.div`
  padding: 2rem;
  background-color: #ffffff;
`;

const SectionTitle = styled.h5`
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 1.5rem 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  .title-content {
    display: flex;
    align-items: center;
    gap: 10px;
    
    &::before {
      content: '';
      display: block;
      width: 4px;
      height: 20px;
      background-color: #3b82f6;
      border-radius: 4px;
    }
  }
`;

const StyledFormGroup = styled(Form.Group)`
  position: relative;
  
  .form-label {
    font-weight: 600;
    color: #475569;
    margin-bottom: 0.5rem;
  }
  
  .form-control, .form-select {
    border-radius: 8px;
    border: 1px solid #cbd5e1;
    padding: 0.5rem 0.75rem;
    transition: all 0.2s;
    background-color: #f8fafc;
    
    &:focus {
      background-color: #ffffff;
      border-color: #3b82f6;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }
  }
`;

const ClearIcon = styled.span`
  cursor: pointer;
  position: absolute;
  right: 15px;
  top: 42px;
  color: #94a3b8;
  transition: color 0.2s;
  
  &:hover {
    color: #ef4444;
  }
`;

const ServiceGroupContainer = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  margin-bottom: 1.5rem;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02);
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #cbd5e1;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  }
`;

const GroupHeader = styled.div`
  background: #f8fafc;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  .group-type-selector {
    display: flex;
    align-items: center;
    gap: 15px;
    flex-grow: 1;
    
    .label {
      font-weight: 700;
      color: #334155;
      font-size: 1.05rem;
      white-space: nowrap;
      display: flex;
      align-items: center;
      gap: 8px;
    }
  }
`;

const ServiceItemContainer = styled.div`
  padding: 1.5rem;
  border-bottom: 1px dashed #e2e8f0;
  background-color: #ffffff;
  
  &:last-child {
    border-bottom: none;
  }
`;

const TierTable = styled.div`
  background-color: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  padding: 1rem;
  margin-top: 1rem;
`;

const CompensationSummary = styled.div`
  background: linear-gradient(to right, #eff6ff, #dbeafe);
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 1rem 1.25rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1.5rem;
  margin-bottom: 1.5rem;
  
  .title {
    color: #1e40af;
    font-weight: 700;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 1.05rem;
  }
  
  .amount {
    font-size: 1.25rem;
    font-weight: 800;
    color: #1d4ed8;
    margin: 0;
  }
`;

const CompensationToggle = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  color: #64748b;
  font-size: 0.85rem;
  font-weight: 600;
  padding: 0 16px;
  height: 38px;
  border: 1px dashed #cbd5e1;
  background: transparent;
  border-radius: 6px;
  transition: all 0.2s ease;
  box-sizing: border-box;
  
  &:hover {
    color: #ca8a04;
    border-color: #fde047;
    background-color: #fefce8;
  }
`;

const CompensationField = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 0 16px;
  height: 38px;
  border: 1px solid #fde047;
  border-radius: 8px;
  background-color: #fefce8;
  box-shadow: 0 1px 2px rgba(234, 179, 8, 0.1);
  box-sizing: border-box;
  
  .label {
    color: #ca8a04;
    font-weight: 600;
    font-size: 0.85rem;
    white-space: nowrap;
  }
  
  .form-control {
    border-color: #fde047;
    background-color: #ffffff;
    max-width: 150px;
    padding: 0.4rem 0.75rem;
    font-weight: 600;
    
    &:focus {
      border-color: #eab308;
      box-shadow: 0 0 0 3px rgba(234, 179, 8, 0.2);
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 1.5rem 2rem;
  background-color: #f8fafc;
  border-top: 1px solid #e2e8f0;
`;

const AddItemButton = styled(Button)`
  border: 1px dashed #cbd5e1;
  background: transparent;
  color: #64748b;
  font-weight: 600;
  padding: 1rem;
  width: 100%;
  border-radius: 0 0 12px 12px;
  
  &:hover:not(:disabled) {
    background: #f8fafc;
    color: #3b82f6;
    border-color: #3b82f6;
  }
  
  &:disabled {
    background: #f1f5f9;
    color: #94a3b8;
  }
`;

const PricingPolicyForm = ({ mode = 'add' }) => {
  const { pricingPolicyUuid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEditMode = mode === 'edit';
  const [isLoading, setIsLoading] = useState(isEditMode);
  
  const queryParams = new URLSearchParams(location.search);
  const originalPricingPolicyUuid = queryParams.get('originalPricingPolicyUuid');

  const [formData, setFormData] = useState({
    policyName: '',
    description: '',
    compensationAmount: '0',
  });
  
  const [nameError, setNameError] = useState('');
  const [descError, setDescError] = useState('');
  
  const [modalCancelShow, setModalCancelShow] = useState(false);
  const [modalConfirmShow, setModalConfirmShow] = useState(false);
  const [modalConfirmCheckShow, setModalConfirmCheckShow] = useState(false);
  
  const [serviceGroups, setServiceGroups] = useState([]);
  const [serviceGroupOptions, setServiceGroupOptions] = useState([]);
  const [serviceItemOptionsMap, setServiceItemOptionsMap] = useState({});
  const [loadingServiceOptions, setLoadingServiceOptions] = useState(true);
  const [loadingServiceItems, setLoadingServiceItems] = useState({});
  const [showPolicyCompensation, setShowPolicyCompensation] = useState(false);

  const unitOptions = [
    { value: 'count', label: '개' },
    { value: 'session', label: '회' },
    { value: 'month', label: '월' },
    { value: 'year', label: '년' },
  ];
  
  const discountUnitOptions = [
    { value: 'percent', label: '%' },
    { value: 'won', label: '원' },
  ];
  
  const calculateEstimatedPrice = (originalPrice, discountAmount, discountUnit) => {
    if (!originalPrice || !discountAmount) return null;
    const price = parseFloat(originalPrice);
    const discount = parseFloat(discountAmount);
    
    if (isNaN(price) || isNaN(discount)) return null;
    
    if (discountUnit === 'percent') return price - (price * discount / 100);
    else if (discountUnit === 'won') return price - discount;
    return null;
  };
  
  const formatPrice = (price) => {
    if (price === null || isNaN(price)) return '';
    return price.toLocaleString('ko-KR');
  };
  
  const calculateTotalCompensation = () => {
    let total = parseFloat(formData.compensationAmount) || 0;
    serviceGroups.forEach(group => {
      total += parseFloat(group.compensationAmount) || 0;
      group.items.forEach(item => {
        total += parseFloat(item.compensationAmount) || 0;
      });
    });
    return total;
  };

  const hasAnyCompensation = () => {
    if (parseFloat(formData.compensationAmount) > 0) return true;
    return serviceGroups.some(group => {
      if (parseFloat(group.compensationAmount) > 0) return true;
      return group.items.some(item => parseFloat(item.compensationAmount) > 0);
    });
  };

  useEffect(() => {
    const fetchServiceGroups = async () => {
      try {
        setLoadingServiceOptions(true);
        const response = await fetchServiceItems(0);
        if (response && response.data) {
          const groups = response.data.map(item => ({
            value: item.serviceItemNumber.toString(),
            label: item.serviceItemName
          }));
          setServiceGroupOptions(groups);
        }
      } catch (error) {
        console.error('Failed to fetch service groups', error);
      } finally {
        setLoadingServiceOptions(false);
      }
    };
    fetchServiceGroups();
  }, []);
  
  const fetchServiceItemsForGroup = async (groupId) => {
    if (serviceItemOptionsMap[groupId]) return serviceItemOptionsMap[groupId];
    setLoadingServiceItems(prev => ({ ...prev, [groupId]: true }));
    try {
      const response = await fetchServiceItems(1, groupId);
      if (response && response.data) {
        const items = response.data.map(item => ({
          value: item.serviceItemNumber.toString(),
          label: item.serviceItemName
        }));
        setServiceItemOptionsMap(prev => ({ ...prev, [groupId]: items }));
        return items;
      }
      return [];
    } catch (error) {
      console.error(`Failed to fetch service items for group ${groupId}`, error);
      return [];
    } finally {
      setLoadingServiceItems(prev => ({ ...prev, [groupId]: false }));
    }
  };
  
  useEffect(() => {
    if (isEditMode && pricingPolicyUuid) {
      const fetchPolicyData = async () => {
        try {
          setIsLoading(true);
          const response = await fetchPricingPolicy(pricingPolicyUuid);
          if (response && response.data) {
            const policyData = response.data;
            const policyCompAmount = policyData.compensationAmount || '0';
            setFormData({
              policyName: policyData.pricingPolicyName,
              description: policyData.description || '',
              compensationAmount: policyCompAmount,
            });
            if (parseFloat(policyCompAmount) > 0) setShowPolicyCompensation(true);
            
            if (policyData.serviceGroupList && policyData.serviceGroupList.length > 0) {
              const formattedGroups = await Promise.all(policyData.serviceGroupList.map(async (group) => {
                if (group.serviceItemNumber) await fetchServiceItemsForGroup(group.serviceItemNumber.toString());
                const groupCompAmount = group.compensationAmount || '0';
                const newGroup = {
                  id: Date.now() + Math.random(),
                  type: group.serviceItemNumber.toString(),
                  compensationAmount: groupCompAmount,
                  showCompensation: parseFloat(groupCompAmount) > 0,
                  items: []
                };
                
                if (group.servicePriceList && group.servicePriceList.length > 0) {
                  group.servicePriceList.forEach(price => {
                    const itemCompAmount = price.compensationAmount || '0';
                    const serviceItem = {
                      id: Date.now() + Math.random(),
                      type: price.serviceItemNumber.toString(),
                      price: price.price.toString(),
                      unit: price.paymentUnit || 'count',
                      compensationAmount: itemCompAmount,
                      showCompensation: parseFloat(itemCompAmount) > 0,
                      tieredPricing: price.serviceTierPriceList && price.serviceTierPriceList.length > 0,
                      tiers: [],
                      roundingMode: price.roundingMode || 'ceil'
                    };
                    
                    if (price.serviceTierPriceList && price.serviceTierPriceList.length > 0) {
                      serviceItem.tiers = price.serviceTierPriceList.map(tier => ({
                        id: Date.now() + Math.random(),
                        startAmount: tier.startAmount.toString(),
                        endAmount: tier.endAmount.toString(),
                        discountAmount: tier.discountAmount.toString(),
                        discountUnit: tier.discountUnit
                      }));
                    }
                    newGroup.items.push(serviceItem);
                  });
                }
                return newGroup;
              }));
              setServiceGroups(formattedGroups);
            }
          } else throw new Error('Invalid response data format');
        } catch (error) {
          console.error('Error fetching policy data:', error);
          alert('요금정책 데이터를 불러오는 중 오류가 발생했습니다.');
        } finally {
          setIsLoading(false);
        }
      };
      fetchPolicyData();
    }
  }, [isEditMode, pricingPolicyUuid]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (name === 'policyName') {
      if (value.length >= 50) setNameError('정책명은 최대 50자까지 입력 가능합니다.');
      else if (value.trim() === '') setNameError('정책명을 입력해주세요.');
      else setNameError('');
    }
    if (name === 'description') {
      if (value.length >= 800) setDescError('설명은 최대 800자까지 입력 가능합니다.');
      else setDescError('');
    }
  };
  
  const getAvailableServiceGroups = () => {
    const selectedGroupTypes = serviceGroups.map(group => group.type).filter(type => type !== '');
    return serviceGroupOptions.filter(option => !selectedGroupTypes.includes(option.value));
  };
  
  const hasReachedMaxGroups = () => serviceGroups.length >= serviceGroupOptions.length;
  
  const addServiceGroup = () => {
    if (hasReachedMaxGroups()) return;
    setServiceGroups([
      ...serviceGroups,
      { id: Date.now(), type: '', compensationAmount: '0', showCompensation: false, items: [] }
    ]);
  };
  
  const removeServiceGroup = (groupId) => setServiceGroups(serviceGroups.filter(group => group.id !== groupId));
  
  const handleGroupTypeChange = async (groupId, value) => {
    if (value) await fetchServiceItemsForGroup(value);
    setServiceGroups(serviceGroups.map(group => {
      if (group.id === groupId) return { ...group, type: value, compensationAmount: group.compensationAmount || '0', items: [] };
      return group;
    }));
  };
  
  const toggleGroupCompensation = (groupId, show) => {
    setServiceGroups(serviceGroups.map(group => {
      if (group.id === groupId) return { ...group, showCompensation: show, compensationAmount: show ? group.compensationAmount : '0' };
      return group;
    }));
  };

  const handleGroupCompensationChange = (groupId, value) => {
    setServiceGroups(serviceGroups.map(group => {
      if (group.id === groupId) return { ...group, compensationAmount: value };
      return group;
    }));
  };

  const toggleItemCompensation = (groupId, itemId, show) => {
    setServiceGroups(serviceGroups.map(group => {
      if (group.id === groupId) {
        return {
          ...group,
          items: group.items.map(item => {
            if (item.id === itemId) return { ...item, showCompensation: show, compensationAmount: show ? item.compensationAmount : '0' };
            return item;
          })
        };
      }
      return group;
    }));
  };

  const togglePolicyCompensation = (show) => {
    setShowPolicyCompensation(show);
    if (!show) setFormData(prev => ({ ...prev, compensationAmount: '0' }));
  };

  const addServiceItem = (groupId) => {
    const group = serviceGroups.find(g => g.id === groupId);
    if (!group || !group.type) return;
    const availableItems = getAvailableServiceItems(groupId, group.type);
    const currentItems = group.items.filter(item => item.type !== '');
    if (availableItems.length === 0 || currentItems.length >= (serviceItemOptionsMap[group.type]?.length || 0)) return;
    
    setServiceGroups(serviceGroups.map(group => {
      if (group.id === groupId) {
        return {
          ...group,
          items: [
            ...group.items,
            { id: Date.now(), type: '', price: '', unit: 'count', compensationAmount: '0', showCompensation: false, tieredPricing: false, tiers: [], roundingMode: 'ceil' }
          ]
        };
      }
      return group;
    }));
  };
  
  const removeServiceItem = (groupId, itemId) => {
    setServiceGroups(serviceGroups.map(group => {
      if (group.id === groupId) return { ...group, items: group.items.filter(item => item.id !== itemId) };
      return group;
    }));
  };

  const handleItemChange = (groupId, itemId, field, value) => {
    setServiceGroups(serviceGroups.map(group => {
      if (group.id === groupId) {
        return {
          ...group,
          items: group.items.map(item => {
            if (item.id === itemId) return { ...item, [field]: value };
            return item;
          })
        };
      }
      return group;
    }));
  };
  
  const toggleTieredPricing = (groupId, itemId, value) => {
    setServiceGroups(serviceGroups.map(group => {
      if (group.id === groupId) {
        return {
          ...group,
          items: group.items.map(item => {
            if (item.id === itemId) return { ...item, tieredPricing: value, tiers: value ? item.tiers : [] };
            return item;
          })
        };
      }
      return group;
    }));
  };
  
  const addPriceTier = (groupId, itemId) => {
    setServiceGroups(serviceGroups.map(group => {
      if (group.id === groupId) {
        return {
          ...group,
          items: group.items.map(item => {
            if (item.id === itemId) {
              return { ...item, tiers: [...item.tiers, { id: Date.now(), startAmount: '', endAmount: '', discountAmount: '', discountUnit: 'percent' }] };
            }
            return item;
          })
        };
      }
      return group;
    }));
  };
  
  const removePriceTier = (groupId, itemId, tierId) => {
    setServiceGroups(serviceGroups.map(group => {
      if (group.id === groupId) {
        return {
          ...group,
          items: group.items.map(item => {
            if (item.id === itemId) return { ...item, tiers: item.tiers.filter(tier => tier.id !== tierId) };
            return item;
          })
        };
      }
      return group;
    }));
  };
  
  const handleTierChange = (groupId, itemId, tierId, field, value) => {
    setServiceGroups(serviceGroups.map(group => {
      if (group.id === groupId) {
        return {
          ...group,
          items: group.items.map(item => {
            if (item.id === itemId) {
              return {
                ...item,
                tiers: item.tiers.map(tier => {
                  if (tier.id === tierId) return { ...tier, [field]: value };
                  return tier;
                })
              };
            }
            return item;
          })
        };
      }
      return group;
    }));
  };

  const getAvailableServiceItems = (groupId, groupType) => {
    const currentGroup = serviceGroups.find(group => group.id === groupId);
    const selectedItemsInGroup = currentGroup ? currentGroup.items.map(item => item.type).filter(type => type !== '') : [];
    return (serviceItemOptionsMap[groupType] || []).filter(option => !selectedItemsInGroup.includes(option.value));
  };

  const hasReachedMaxItems = (groupId) => {
    const group = serviceGroups.find(g => g.id === groupId);
    if (!group || !group.type) return true;
    const totalAvailableItems = serviceItemOptionsMap[group.type]?.length || 0;
    const currentItemCount = group.items.filter(item => item.type !== '').length;
    const emptyItemCount = group.items.filter(item => item.type === '').length;
    return currentItemCount + emptyItemCount >= totalAvailableItems;
  };
  
  const isFormValid = () => {
    if (formData.policyName.trim() === '') return false;
    if (nameError || descError) return false;
    if (serviceGroups.length === 0) return false;

    for (const group of serviceGroups) {
      if (!group.type || group.items.length === 0) return false;
      for (const item of group.items) {
        if (!item.type || !item.price) return false;
        if (item.tieredPricing && item.tiers.length > 0) {
          for (const tier of item.tiers) {
            if (tier.startAmount === '' || tier.endAmount === '' || tier.discountAmount === '') return false;
          }
          // Layer 1: tier 구조 무결성 검증 (gap/overlap/시작값)
          if (validateTierStructure(item.tiers) !== null) return false;
        }
      }
    }
    return true;
  };

  /**
   * 구간별 요금 목록 검증.
   * 위반 시 사용자에게 표시할 한국어 에러 메시지 반환, 정상이면 null.
   * 규칙: 첫 tier start=1, 각 tier start<=end, 연속 tier는 next.start === prev.end+1.
   */
  const validateTierStructure = (tiers) => {
    if (!tiers || tiers.length === 0) return null;
    const sorted = tiers.map(t => ({
      startAmount: parseInt(t.startAmount, 10),
      endAmount: parseInt(t.endAmount, 10)
    })).filter(t => !isNaN(t.startAmount) && !isNaN(t.endAmount));
    if (sorted.length === 0) return null;

    // 정렬 (사용자가 순서 뒤섞어 입력해도 정상 판정 가능하게)
    sorted.sort((a, b) => a.startAmount - b.startAmount);

    if (sorted[0].startAmount !== 1) {
      return `첫 번째 구간의 시작값은 반드시 1 이어야 합니다. (현재: ${sorted[0].startAmount})`;
    }
    let prevEnd = 0;
    for (let i = 0; i < sorted.length; i++) {
      const { startAmount, endAmount } = sorted[i];
      if (startAmount > endAmount) {
        return `구간 ${i + 1}: 시작값(${startAmount})이 종료값(${endAmount})보다 큽니다.`;
      }
      if (i > 0) {
        if (startAmount > prevEnd + 1) {
          return `구간 ${i} 과 구간 ${i + 1} 사이에 비어있는 구간이 있습니다. (${prevEnd + 1}~${startAmount - 1} 누락)`;
        }
        if (startAmount <= prevEnd) {
          return `구간 ${i} 과 구간 ${i + 1} 이 겹칩니다. (${startAmount}~${prevEnd} 중복)`;
        }
      }
      prevEnd = endAmount;
    }
    return null;
  };

  const formatDataForSubmission = () => {
    const serviceGroupList = serviceGroups.map(group => {
      const serviceGroup = {
        serviceItemNumber: parseInt(group.type),
        compensationAmount: group.compensationAmount || '0',
        servicePriceList: []
      };

      if (group.items.length > 0) {
        serviceGroup.servicePriceList = group.items.map(item => {
          return {
            serviceItemNumber: parseInt(item.type),
            price: item.price,
            paymentUnit: item.unit,
            compensationAmount: item.compensationAmount || '0',
            roundingMode: item.roundingMode || 'ceil',
            serviceTierPriceList: item.tieredPricing && item.tiers.length > 0 ?
              item.tiers.map(tier => ({
                startAmount: tier.startAmount,
                endAmount: tier.endAmount,
                discountAmount: tier.discountAmount,
                discountUnit: tier.discountUnit
              })) : []
          };
        });
      }
      return serviceGroup;
    });
    
    const pricingPolicy = {
      pricingPolicyName: formData.policyName,
      description: formData.description,
      compensationAmount: formData.compensationAmount || '0',
      serviceGroupList: serviceGroupList
    };
    
    if (isEditMode && pricingPolicyUuid) {
      pricingPolicy.pricingPolicyUuid = pricingPolicyUuid;
      if (originalPricingPolicyUuid) pricingPolicy.originalPricingPolicyUuid = originalPricingPolicyUuid;
    }
    return pricingPolicy;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setModalConfirmShow(true);
  };
  
  const handleConfirm = async () => {
    try {
      const pricingPolicyData = formatDataForSubmission();
      const response = await createPricingPolicy(pricingPolicyData);
      
      if (response && response.data) {
        setModalConfirmShow(false);
        setModalConfirmCheckShow(true);
      } else throw new Error('응답 데이터가 없습니다.');
    } catch (error) {
      console.error(`요금정책 ${isEditMode ? '수정' : '등록'} 중 오류 발생:`, error);
      alert(`요금정책 ${isEditMode ? '수정' : '등록'} 중 오류가 발생했습니다.`);
      setModalConfirmShow(false);
    }
  };
  
  const handleConfirmComplete = () => {
    setModalConfirmCheckShow(false);
    navigate('/safedb/SA/pricingPolicy');
  };
  
  const handleCancel = () => navigate('/safedb/SA/pricingPolicy');
  const handleBackClick = () => navigate('/safedb/SA/pricingPolicy');
  
  const isLoadingServiceItemsForGroup = (groupType) => !!loadingServiceItems[groupType];

  if (isLoading) {
    return (
      <PageContainer>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <div className="text-center">
            <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
            <p className="mt-3 text-500 fw-semi-bold">요금정책 폼을 불러오는 중입니다...</p>
          </div>
        </div>
      </PageContainer>
    );
  }
  
  return (
    <PageContainer>
      <HeaderSection>
        <BackButton onClick={handleBackClick}>
          <FontAwesomeIcon icon={faArrowLeft} />
          목록으로 돌아가기
        </BackButton>
      </HeaderSection>

      <StyledCard>
        <CardHeader>
          <PolicyTitle>
            <FontAwesomeIcon icon={faTag} className="text-primary" />
            요금정책 {isEditMode ? '수정' : '등록'}
          </PolicyTitle>
        </CardHeader>
        
        <Form onSubmit={handleSubmit}>
          <FormSection>
            <SectionTitle>
              <div className="title-content">기본 정보</div>
            </SectionTitle>
            
            <Row>
              <Col md={12}>
                <StyledFormGroup className="mb-4">
                  <Form.Label>정책명 <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="policyName"
                    value={formData.policyName}
                    onChange={handleChange}
                    placeholder="요금정책 이름을 입력하세요"
                    maxLength="50"
                  />
                  {formData.policyName && (
                    <ClearIcon onClick={() => handleValueDelete('policyName', formData, setFormData)}>
                      <AiFillCloseCircle />
                    </ClearIcon>
                  )}
                  {nameError && <div className="text-danger mt-1 fs--1">{nameError}</div>}
                </StyledFormGroup>
              </Col>
            </Row>
            
            <Row>
              <Col>
                <StyledFormGroup className="mb-4">
                  <Form.Label>설명</Form.Label>
                  <Form.Control
                    as="textarea"
                    name="description"
                    rows={3}
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="요금정책에 대한 설명을 입력하세요"
                    maxLength="800"
                  />
                  {formData.description && (
                    <ClearIcon onClick={() => handleValueDelete('description', formData, setFormData)}>
                      <AiFillCloseCircle />
                    </ClearIcon>
                  )}
                  {descError && <div className="text-danger mt-1 fs--1">{descError}</div>}
                </StyledFormGroup>
              </Col>
            </Row>

            <div className="mb-4">
              {showPolicyCompensation ? (
                <CompensationField>
                  <span className="label">정책 레벨 보전금액(원)</span>
                  <Form.Control
                    type="number"
                    min="0"
                    name="compensationAmount"
                    value={formData.compensationAmount}
                    onChange={handleChange}
                    placeholder="0"
                  />
                  <Button variant="link" className="text-danger p-0 ms-2" onClick={() => togglePolicyCompensation(false)} title="보전금액 제거">
                    <FontAwesomeIcon icon={faTimes} />
                  </Button>
                </CompensationField>
              ) : (
                <CompensationToggle type="button" onClick={() => togglePolicyCompensation(true)}>
                  <FontAwesomeIcon icon={faPlus} />
                  정책 보전금액 설정
                </CompensationToggle>
              )}
            </div>
            
            <hr className="my-5 border-200" />

            <SectionTitle>
              <div className="title-content">서비스 구성 상세</div>
              <ActionButton 
                variant="outline-primary" 
                size="sm" 
                onClick={addServiceGroup}
                disabled={hasReachedMaxGroups() || loadingServiceOptions}
              >
                <FontAwesomeIcon icon={faPlus} />
                서비스 그룹 추가
              </ActionButton>
            </SectionTitle>

            {loadingServiceOptions ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" size="sm" className="me-2" />
                서비스 옵션 로딩 중...
              </div>
            ) : serviceGroups.length === 0 ? (
              <div className="text-center py-5 bg-light rounded-3 border border-dashed text-500">
                <FontAwesomeIcon icon={faCubes} size="2x" className="mb-3 text-400" />
                <p className="mb-0">서비스 그룹을 추가하여 정책을 구성하세요.</p>
              </div>
            ) : (
              serviceGroups.map((group, groupIndex) => (
                <ServiceGroupContainer key={group.id}>
                  <GroupHeader>
                    <div className="group-type-selector">
                      <div className="label">
                        <FontAwesomeIcon icon={faLayerGroup} className="text-primary opacity-75" />
                        그룹 {groupIndex + 1}
                      </div>
                      <Form.Select
                        value={group.type}
                        onChange={(e) => handleGroupTypeChange(group.id, e.target.value)}
                        style={{ maxWidth: '300px', fontWeight: 600 }}
                      >
                        <option value="">서비스 그룹 선택</option>
                        {serviceGroupOptions
                          .filter(option => option.value === group.type || !serviceGroups.map(g => g.type).filter(t => t !== '').includes(option.value))
                          .map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))
                        }
                      </Form.Select>
                      
                      {group.showCompensation ? (
                        <CompensationField className="ms-2">
                          <span className="label">보전금액(원)</span>
                          <Form.Control
                            type="number"
                            min="0"
                            size="sm"
                            value={group.compensationAmount}
                            onChange={(e) => handleGroupCompensationChange(group.id, e.target.value)}
                            placeholder="0"
                          />
                          <Button variant="link" className="text-danger p-0 ms-1" onClick={() => toggleGroupCompensation(group.id, false)}>
                            <FontAwesomeIcon icon={faTimes} />
                          </Button>
                        </CompensationField>
                      ) : (
                        <CompensationToggle type="button" onClick={() => toggleGroupCompensation(group.id, true)} className="ms-2">
                          <FontAwesomeIcon icon={faPlus} />
                          보전금액
                        </CompensationToggle>
                      )}
                    </div>
                    
                    <Button variant="outline-danger" onClick={() => removeServiceGroup(group.id)} style={{ padding: '0.4rem 1rem', borderRadius: '6px', fontWeight: 600 }}>
                      <FontAwesomeIcon icon={faTrash} className="me-2" />
                      그룹 삭제
                    </Button>
                  </GroupHeader>
                  
                  {group.type && (
                    <div>
                      {group.items.length === 0 ? (
                        <div className="p-4 text-center text-500 fs--1">항목을 추가해주세요.</div>
                      ) : (
                        group.items.map((item, itemIndex) => (
                          <ServiceItemContainer key={item.id}>
                            <Row className="mb-3 align-items-end">
                              <Col md={4}>
                                <StyledFormGroup>
                                  <Form.Label>서비스 항목</Form.Label>
                                  {isLoadingServiceItemsForGroup(group.type) ? (
                                    <div className="d-flex align-items-center py-2">
                                      <Spinner animation="border" size="sm" variant="primary" className="me-2" />
                                      <span className="text-500">로딩 중...</span>
                                    </div>
                                  ) : (
                                    <Form.Select
                                      value={item.type}
                                      onChange={(e) => handleItemChange(group.id, item.id, 'type', e.target.value)}
                                    >
                                      <option value="">항목 선택</option>
                                      {[
                                        ...(item.type ? serviceItemOptionsMap[group.type]?.filter(option => option.value === item.type) || [] : []),
                                        ...getAvailableServiceItems(group.id, group.type)
                                      ].map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                      ))}
                                    </Form.Select>
                                  )}
                                </StyledFormGroup>
                              </Col>
                              <Col md={3}>
                                <StyledFormGroup>
                                  <Form.Label>단가(원)</Form.Label>
                                  <Form.Control
                                    type="number"
                                    min="0"
                                    value={item.price}
                                    onChange={(e) => handleItemChange(group.id, item.id, 'price', e.target.value)}
                                    placeholder="0"
                                  />
                                </StyledFormGroup>
                              </Col>
                              <Col md={3}>
                                <StyledFormGroup>
                                  <Form.Label>단위</Form.Label>
                                  <Form.Select
                                    value={item.unit}
                                    onChange={(e) => handleItemChange(group.id, item.id, 'unit', e.target.value)}
                                  >
                                    {unitOptions.map((option) => (
                                      <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                  </Form.Select>
                                </StyledFormGroup>
                              </Col>
                              <Col md={2}>
                                <Button variant="outline-danger" onClick={() => removeServiceItem(group.id, item.id)} className="w-100" style={{ height: '38px', borderRadius: '8px', fontWeight: 600, padding: '0.4rem 0.75rem', marginTop: '29px' }}>
                                  항목 삭제
                                </Button>
                              </Col>
                            </Row>

                            <Row className="mb-3">
                              <Col md={6}>
                                <StyledFormGroup>
                                  <Form.Label className="d-flex align-items-center" style={{ gap: '6px' }}>
                                    최종 수량 반영 방법
                                    <OverlayTrigger
                                      placement="top"
                                      delay={{ show: 150, hide: 300 }}
                                      overlay={
                                        <Tooltip id={`rounding-mode-tooltip-${item.id}`}>
                                          측정된 사용량(평균 에이전트 수, 정책서버 수 등)의 소수점을 어떻게 처리해 최종 청구 수량으로 반영할지 결정합니다.
                                          <br />
                                          예: 평균 3.1대 사용 시 올림=4대, 반올림=3대, 내림=3대.
                                        </Tooltip>
                                      }
                                    >
                                      <span style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer', color: '#94a3b8' }}>
                                        <BsInfoCircle />
                                      </span>
                                    </OverlayTrigger>
                                  </Form.Label>
                                  <Form.Select
                                    value={item.roundingMode || 'ceil'}
                                    onChange={(e) => handleItemChange(group.id, item.id, 'roundingMode', e.target.value)}
                                  >
                                    <option value="ceil">올림 (Ceil) — 소수점 이하 올려서 항상 더 많은 수량</option>
                                    <option value="round">반올림 (Round) — 0.5 미만은 내림, 0.5 이상은 올림</option>
                                    <option value="floor">내림 (Floor) — 소수점 이하 버려서 항상 더 적은 수량</option>
                                  </Form.Select>
                                </StyledFormGroup>
                              </Col>
                            </Row>

                            <div className="mb-3">
                              {item.showCompensation ? (
                                <CompensationField>
                                  <span className="label">항목 보전금액(원)</span>
                                  <Form.Control
                                    type="number"
                                    min="0"
                                    size="sm"
                                    value={item.compensationAmount}
                                    onChange={(e) => handleItemChange(group.id, item.id, 'compensationAmount', e.target.value)}
                                    placeholder="0"
                                  />
                                  <Button variant="link" className="text-danger p-0 ms-1" onClick={() => toggleItemCompensation(group.id, item.id, false)}>
                                    <FontAwesomeIcon icon={faTimes} />
                                  </Button>
                                </CompensationField>
                              ) : (
                                <CompensationToggle type="button" onClick={() => toggleItemCompensation(group.id, item.id, true)}>
                                  <FontAwesomeIcon icon={faPlus} />
                                  항목 보전금액 추가
                                </CompensationToggle>
                              )}
                            </div>

                            <div className="mb-3 d-flex align-items-center" style={{ minHeight: '38px' }}>
                              <Form.Check 
                                type="switch"
                                id={`tiered-pricing-${item.id}`}
                                checked={item.tieredPricing}
                                onChange={(e) => toggleTieredPricing(group.id, item.id, e.target.checked)}
                                label={<span className="fw-semi-bold d-inline-block" style={{ marginTop: '2px', marginLeft: '4px' }}>구간별 할인 활성화</span>}
                                className="custom-switch-primary mb-0 d-flex align-items-center"
                              />
                            </div>

                            {item.tieredPricing && (
                              <TierTable>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                  <h6 className="mb-0 fw-bold text-600">구간 할인 설정</h6>
                                  <Button variant="info" size="sm" onClick={() => addPriceTier(group.id, item.id)}>
                                    <FontAwesomeIcon icon={faPlus} className="me-1" />
                                    구간 추가
                                  </Button>
                                </div>
                                
                                {(() => {
                                  const tierError = validateTierStructure(item.tiers);
                                  return tierError ? (
                                    <div className="text-danger mb-2 fs--1" style={{ fontWeight: 600 }}>
                                      ⚠ {tierError}
                                    </div>
                                  ) : null;
                                })()}

                                {item.tiers.length === 0 ? (
                                  <div className="text-center py-3 text-400 fs--1">등록된 구간이 없습니다.</div>
                                ) : (
                                  item.tiers.map((tier, tierIndex) => (
                                    <Row key={tier.id} className="mb-2 align-items-end gx-2">
                                      <Col>
                                        <Form.Control size="sm" type="number" min="0" placeholder="시작" value={tier.startAmount} onChange={(e) => handleTierChange(group.id, item.id, tier.id, 'startAmount', e.target.value)} />
                                      </Col>
                                      <Col xs="auto" className="pb-1 text-400">~</Col>
                                      <Col>
                                        <Form.Control size="sm" type="number" min="0" placeholder="종료" value={tier.endAmount} onChange={(e) => handleTierChange(group.id, item.id, tier.id, 'endAmount', e.target.value)} />
                                      </Col>
                                      <Col>
                                        <Form.Control size="sm" type="number" min="0" placeholder="할인수치" value={tier.discountAmount} onChange={(e) => handleTierChange(group.id, item.id, tier.id, 'discountAmount', e.target.value)} />
                                      </Col>
                                      <Col>
                                        <Form.Select size="sm" value={tier.discountUnit} onChange={(e) => handleTierChange(group.id, item.id, tier.id, 'discountUnit', e.target.value)}>
                                          {discountUnitOptions.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                        </Form.Select>
                                      </Col>
                                      <Col>
                                        <div className="form-control form-control-sm bg-200 text-end text-primary fw-bold border-0">
                                          {tier.discountAmount && item.price ? formatPrice(calculateEstimatedPrice(item.price, tier.discountAmount, tier.discountUnit)) : '-'}
                                        </div>
                                      </Col>
                                      <Col xs="auto">
                                        <Button variant="light" size="sm" className="text-danger" onClick={() => removePriceTier(group.id, item.id, tier.id)}>
                                          <FontAwesomeIcon icon={faMinus} />
                                        </Button>
                                      </Col>
                                    </Row>
                                  ))
                                )}
                              </TierTable>
                            )}
                          </ServiceItemContainer>
                        ))
                      )}
                      
                      <AddItemButton 
                        type="button" 
                        onClick={() => addServiceItem(group.id)}
                        disabled={hasReachedMaxItems(group.id)}
                      >
                        <FontAwesomeIcon icon={faPlus} className="me-2" />
                        {hasReachedMaxItems(group.id) ? '모든 항목 추가됨' : '서비스 항목 추가'}
                      </AddItemButton>
                    </div>
                  )}
                </ServiceGroupContainer>
              ))
            )}

            {hasAnyCompensation() && (
              <CompensationSummary>
                <div className="title">
                  <FontAwesomeIcon icon={faWonSign} size="lg" />
                  기본 청구 금액 (보전금액 총합)
                </div>
                <div className="amount">
                  {formatPrice(calculateTotalCompensation())}원
                </div>
              </CompensationSummary>
            )}

          </FormSection>
          
          <ButtonGroup>
            <Button variant="light" onClick={() => setModalCancelShow(true)} className="px-4 fw-semi-bold text-600">
              취소
            </Button>
            <ActionButton variant="primary" type="submit" disabled={!isFormValid()} className="px-4">
              <FontAwesomeIcon icon={faSave} />
              {isEditMode ? '변경사항 저장' : '정책 등록'}
            </ActionButton>
          </ButtonGroup>
        </Form>
      </StyledCard>
      
      <CheckOutModal
        show={modalCancelShow}
        onHide={() => setModalCancelShow(false)}
        onConfirm={handleCancel}
        title="작업 취소"
        body="진행 중인 작업을 취소하고 목록으로 돌아가시겠습니까? 작성 중인 내용은 저장되지 않습니다."
        variant="danger"
        cancelText="계속 작성"
        confirmText="나가기"
        CancelButton={true}
        SubmitButton={true}
      />
      
      <CheckOutModal
        show={modalConfirmShow}
        onHide={() => setModalConfirmShow(false)}
        onConfirm={handleConfirm}
        title="정책 저장"
        variant="primary"
        body={`요금정책을 ${isEditMode ? '수정' : '새로 등록'}하시겠습니까?`}
        cancelText="취소"
        confirmText="확인"
        CancelButton={true}
        SubmitButton={true}
      />
      
      <CheckOutModal
        show={modalConfirmCheckShow}
        onHide={handleConfirmComplete}
        onConfirm={handleConfirmComplete}
        title="저장 완료"
        variant="success"
        body={`요금정책이 성공적으로 ${isEditMode ? '수정' : '등록'}되었습니다.`}
        confirmText="목록으로 이동"
        SubmitButton={true}
      />
    </PageContainer>
  );
};

export default PricingPolicyForm;
